import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { Subject, Subscription, timer, interval } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { CMS_COGNITO_CLIENT_IDS, CMS_PLATFORM_ORIGINS } from './cms-auth.tokens';
export interface CmsSsoPayload {
  type: 'PLATFORM_COGNITO_SSO';
  token: string;
  user: {
    permissions?: string[];
    role?: string;
    company?: string;
    companyId?: number;
    /** Canonical Cognito user ID (available even for Entra-federated sessions) */
    cognitoId?: string | null;
    /** User email from the platform */
    email?: string | null;
    /** True when the session was initiated via a federated IdP (e.g. Entra ID) */
    isFederated?: boolean;
  };
}
interface JwtPayload {
  exp?: number;
  client_id?: string;
  aud?: string | string[];
  [key: string]: unknown;
}
/** How many seconds before expiration to request a fresh token */
const REFRESH_BEFORE_EXPIRY_S = 60;
/** Interval (ms) between PLATFORM_CHILD_READY retry signals */
const READY_RETRY_INTERVAL_MS = 1500;
/** Max number of PLATFORM_CHILD_READY retries before giving up */
const READY_MAX_RETRIES = 5;
@Injectable({ providedIn: 'root' })
export class CmsTokenBridgeService implements OnDestroy {
  private readonly allowedOrigins = inject(CMS_PLATFORM_ORIGINS);
  private readonly allowedClientIds = inject(CMS_COGNITO_CLIENT_IDS);
  private readonly _token = signal<string | null>(null);
  private readonly _ssoPayload = signal<CmsSsoPayload | null>(null);
  private readonly _tokenReceived$ = new Subject<void>();
  private refreshTimerSub?: Subscription;
  private readyRetrySub?: Subscription;
  readonly token = this._token.asReadonly();
  readonly ssoPayload = this._ssoPayload.asReadonly();
  readonly tokenReceived$ = this._tokenReceived$.asObservable();
  constructor() {
    window.addEventListener('message', (event: MessageEvent) => {
      this.handleMessage(event);
    });
    // Signal to the parent platform that this app is ready to receive the SSO token.
    // Retry periodically in case the parent hasn't set up its listener yet
    // (can happen with federated IdP flows where token retrieval is slower).
    if (this.isEmbedded()) {
      this.sendReadyWithRetry();
    }
  }
  ngOnDestroy(): void {
    this.refreshTimerSub?.unsubscribe();
    this.readyRetrySub?.unsubscribe();
  }
  getToken(): string | null {
    return this._token();
  }
  isEmbedded(): boolean {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }
  waitForToken$() {
    return this.tokenReceived$;
  }
  /**
   * Sends PLATFORM_CHILD_READY immediately and retries every READY_RETRY_INTERVAL_MS
   * until a token is received or max retries is reached.
   */
  private sendReadyWithRetry(): void {
    // Send immediately
    window.parent.postMessage({ type: 'PLATFORM_CHILD_READY' }, '*');
    console.log('[CmsAuth] Sent PLATFORM_CHILD_READY to parent (attempt 1)');
    let attempt = 1;
    this.readyRetrySub = interval(READY_RETRY_INTERVAL_MS)
      .pipe(
        takeWhile(() => !this._token() && attempt < READY_MAX_RETRIES)
      )
      .subscribe(() => {
        attempt++;
        console.log(`[CmsAuth] Retrying PLATFORM_CHILD_READY (attempt ${attempt})`);
        window.parent.postMessage({ type: 'PLATFORM_CHILD_READY' }, '*');
      });
  }
  private handleMessage(event: MessageEvent): void {
    if (!this.isOriginAllowed(event.origin)) {
      return;
    }
    const data = event.data as Partial<CmsSsoPayload>;
    if (data?.type !== 'PLATFORM_COGNITO_SSO' || !data.token) {
      return;
    }
    if (!this.isValidToken(data.token)) {
      console.warn('[CmsAuth] Received token failed validation — rejected.', {
        origin: event.origin,
        isFederated: data.user?.isFederated ?? false,
        hasCognitoId: !!data.user?.cognitoId
      });
      return;
    }
    this._token.set(data.token);
    this._ssoPayload.set(data as CmsSsoPayload);
    this._tokenReceived$.next();
    // Stop retrying PLATFORM_CHILD_READY once we have a token
    this.readyRetrySub?.unsubscribe();
    // Schedule a refresh request before this token expires
    this.scheduleRefresh(data.token);
  }
  private scheduleRefresh(token: string): void {
    this.refreshTimerSub?.unsubscribe();
    if (!this.isEmbedded()) {
      return;
    }
    const payload = this.decodeJwtPayload(token);
    if (!payload?.['exp']) {
      return;
    }
    const exp = payload['exp'] as number;
    const now = Math.floor(Date.now() / 1000);
    const msUntilRefresh = Math.max((exp - now - REFRESH_BEFORE_EXPIRY_S) * 1000, 0);
    console.log(`[CmsAuth] Token refresh scheduled in ${Math.round(msUntilRefresh / 1000)}s`);
    this.refreshTimerSub = timer(msUntilRefresh).subscribe(() => {
      console.log('[CmsAuth] Requesting token refresh from platform');
      window.parent.postMessage({ type: 'PLATFORM_TOKEN_REFRESH_REQUEST' }, '*');
    });
  }
  private isOriginAllowed(origin: string): boolean {
    return (this.allowedOrigins as string[]).includes(origin);
  }
  private isValidToken(token: string): boolean {
    const payload = this.decodeJwtPayload(token);
    if (!payload) {
      return false;
    }
    // Validate that client_id or aud matches at least one known platform client ID
    if (this.allowedClientIds.length > 0) {
      const tokenClientId = payload['client_id'] as string | undefined;
      const audRaw = payload['aud'];
      const audList: string[] = Array.isArray(audRaw)
        ? (audRaw as string[])
        : audRaw
        ? [audRaw as string]
        : [];
      const allTokenIds = [
        ...(tokenClientId ? [tokenClientId] : []),
        ...audList
      ];
      const isKnownClient = allTokenIds.some((id) =>
        (this.allowedClientIds as string[]).includes(id)
      );
      if (!isKnownClient) {
        console.warn('[CmsAuth] Token client_id/aud does not match any known platform client ID.');
        return false;
      }
    }
    // Validate expiration
    const exp = payload['exp'] as number | undefined;
    if (exp !== undefined && exp < Math.floor(Date.now() / 1000)) {
      console.warn('[CmsAuth] Token has expired.');
      return false;
    }
    return true;
  }
  private decodeJwtPayload(token: string): JwtPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(
        base64.length + ((4 - (base64.length % 4)) % 4),
        '='
      );
      const json = atob(padded);
      return JSON.parse(json) as JwtPayload;
    } catch {
      return null;
    }
  }
}
